import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { PlacesAutocompleteInput } from '../../components/inputs/PlacesAutocompleteInput';
import {
  CalendarDays,
  Clock,
  Car,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  DollarSign,
} from 'lucide-react';

const formatDate = date => {
  if (!date) return '';
  const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
  const formattedDate = date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return `${dayName}, ${formattedDate}`;
};

const formatTime = date => {
  if (!date) return '';
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

export default function FlexyRideCreateSeat() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = usePublicAuth();
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [listingId, setListingId] = useState(null);

  const [formData, setFormData] = useState({
    from: '',
    to: '',
    date: '',
    time: '',
    seats: '',
    price: '',
    carBrand: '',
    carModel: '',
    carYear: '',
    note: '',
    smoking: 'no',
    pets: 'no',
    luggage: 'small',
    comfort: 'flexible',
    agreeContact: false,
    agreePolicy: false,
  });

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);

  const steps = ['route', 'preferences', 'dateTime', 'summary'];
  const totalSteps = steps.length;

  // Load listing data for edit mode
  useEffect(() => {
    const state = location.state;
    if (state?.editMode && state?.listingData) {
      const listing = state.listingData;
      setEditMode(true);
      setListingId(state.listingId);

      // Parse date and time
      let parsedDate = new Date();
      let parsedTime = new Date();
      if (listing.date) {
        const dateMatch = listing.date.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
        if (dateMatch) {
          const [, month, day, year] = dateMatch;
          parsedDate = new Date(
            parseInt(year),
            parseInt(month) - 1,
            parseInt(day),
          );
        }
      }
      if (listing.time) {
        const timeMatch = listing.time.match(/(\d{1,2}):(\d{1,2})/);
        if (timeMatch) {
          const [, hour, minute] = timeMatch;
          parsedTime = new Date();
          parsedTime.setHours(parseInt(hour), parseInt(minute));
        }
      }

      setFormData({
        from: listing.from || '',
        to: listing.to || '',
        date: listing.date || '',
        time: listing.time || '',
        seats: listing.seats?.toString() || '',
        price: listing.price?.toString() || '',
        carBrand: listing.carBrand || '',
        carModel: listing.carModel || '',
        carYear: listing.carYear || '',
        note: listing.note || '',
        smoking: listing.smoking || 'no',
        pets: listing.pets || 'no',
        luggage: listing.luggage || 'small',
        comfort: listing.comfort || 'flexible',
        agreeContact: false,
        agreePolicy: false,
      });
      setSelectedDate(parsedDate);
      setSelectedTime(parsedTime);
    }
  }, [location.state]);

  const handleFromSelect = location => {
    setFormData(prev => ({
      ...prev,
      from: location?.description || '',
    }));
  };

  const handleToSelect = location => {
    setFormData(prev => ({
      ...prev,
      to: location?.description || '',
    }));
  };

  const validateCurrentStep = () => {
    const step = steps[currentStep];
    if (!step) return true;

    switch (step) {
      case 'route':
        if (!formData.from || !formData.to || !formData.seats) {
          alert(t('flexyRideSeat.validation.routeRequired'));
          return false;
        }
        if (formData.from === formData.to) {
          alert(t('flexyRideSeat.validation.originDestinationSame'));
          return false;
        }
        if (!formData.seats || Number(formData.seats) <= 0) {
          alert(t('flexyRideSeat.validation.seatsInvalid'));
          return false;
        }
        return true;
      case 'preferences':
        if (
          !formData.carBrand ||
          !formData.carModel ||
          !formData.carYear ||
          Number(formData.carYear) <= 0
        ) {
          alert(t('flexyRideSeat.validation.carInfoRequired'));
          return false;
        }
        return true;
      case 'dateTime':
        if (!formData.date || !formData.time || !formData.price) {
          alert(t('flexyRideSeat.validation.dateTimePriceRequired'));
          return false;
        }
        if (!formData.price || Number(formData.price) <= 0) {
          alert(t('flexyRideSeat.validation.priceInvalid'));
          return false;
        }
        return true;
      case 'summary':
        if (!formData.agreeContact || !formData.agreePolicy) {
          alert(t('flexyRideSeat.validation.agreementsRequired'));
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (!validateCurrentStep()) {
      return;
    }
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!user?.uid) {
      alert(t('flexyRideSeat.validation.loginRequired'));
      return;
    }
    if (!validateCurrentStep()) {
      return;
    }

    try {
      setIsSubmitting(true);
      const updateData = {
        mode: 'ride',
        type: 'seat',
        title: 'Seat listing',
        from: formData.from,
        to: formData.to,
        date: formData.date,
        time: formData.time,
        seats: Number(formData.seats) || 1,
        smoking: formData.smoking,
        pets: formData.pets,
        luggage: formData.luggage,
        comfort: formData.comfort,
        carBrand: formData.carBrand || '',
        carModel: formData.carModel || '',
        carYear: formData.carYear || '',
        price: Number(formData.price) || 0,
        currency: 'CAD',
        note: formData.note,
        updatedAt: serverTimestamp(),
      };

      if (editMode && listingId) {
        await updateDoc(doc(db, 'ride_listings', listingId), updateData);
        alert(t('flexyRideSeat.successUpdate'));
      } else {
        await addDoc(collection(db, 'ride_listings'), {
          ...updateData,
          createdBy: user.uid,
          createdAt: serverTimestamp(),
        });
        alert(t('flexyRideSeat.successCreate'));
      }
      navigate('/flexy-ride/my-seat-listings');
    } catch (error) {
      console.error('Error saving listing:', error);
      alert(t('flexyRideSeat.errorGeneric'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    const step = steps[currentStep];
    if (!step) return null;

    switch (step) {
      case 'route':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-black text-gray-900 dark:text-white">
              {t('flexyRideSeat.routeTitle')}
            </h2>
            <div className="space-y-4">
              <PlacesAutocompleteInput
                placeholder={t('home.from')}
                onSelect={handleFromSelect}
                onInputChange={value => {
                  if (!value) {
                    setFormData(prev => ({ ...prev, from: '' }));
                  }
                }}
                value={formData.from ? { description: formData.from } : null}
              />
              <PlacesAutocompleteInput
                placeholder={t('home.to')}
                onSelect={handleToSelect}
                onInputChange={value => {
                  if (!value) {
                    setFormData(prev => ({ ...prev, to: '' }));
                  }
                }}
                value={formData.to ? { description: formData.to } : null}
              />
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-slate-400 mb-2">
                  {t('flexyRideSeat.seats')}
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.seats}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, seats: e.target.value }))
                  }
                  className="w-full rounded-2xl border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-800 px-4 py-3 text-lg text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none focus:border-black dark:focus:border-yellow-500"
                  placeholder={t('flexyRideSeat.seatsPlaceholder')}
                />
              </div>
            </div>
          </div>
        );

      case 'preferences':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-black text-gray-900 dark:text-white">
              {t('flexyRideSeat.preferencesTitle')}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-slate-400 mb-2">
                  {t('flexyRideSeat.smoking')}
                </label>
                <div className="flex gap-2">
                  {['no', 'yes', 'flexible'].map(val => (
                    <button
                      key={val}
                      type="button"
                      onClick={() =>
                        setFormData(prev => ({ ...prev, smoking: val }))
                      }
                      className={`flex-1 rounded-xl px-4 py-3 text-sm font-semibold transition ${
                        formData.smoking === val
                          ? 'bg-black dark:bg-yellow-500 text-yellow-400 dark:text-black'
                          : 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700'
                      }`}
                    >
                      {val === 'no'
                        ? t('flexyRideSeat.no')
                        : val === 'yes'
                        ? t('flexyRideSeat.yes')
                        : t('flexyRideSeat.flexible')}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-slate-400 mb-2">
                  {t('flexyRideSeat.pets')}
                </label>
                <div className="flex gap-2">
                  {['no', 'yes', 'flexible'].map(val => (
                    <button
                      key={val}
                      type="button"
                      onClick={() =>
                        setFormData(prev => ({ ...prev, pets: val }))
                      }
                      className={`flex-1 rounded-xl px-4 py-3 text-sm font-semibold transition ${
                        formData.pets === val
                          ? 'bg-black dark:bg-yellow-500 text-yellow-400 dark:text-black'
                          : 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700'
                      }`}
                    >
                      {val === 'no'
                        ? t('flexyRideSeat.no')
                        : val === 'yes'
                        ? t('flexyRideSeat.yes')
                        : t('flexyRideSeat.flexible')}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-slate-400 mb-2">
                  {t('flexyRideSeat.luggage')}
                </label>
                <div className="flex gap-2">
                  {['small', 'medium', 'large'].map(val => (
                    <button
                      key={val}
                      type="button"
                      onClick={() =>
                        setFormData(prev => ({ ...prev, luggage: val }))
                      }
                      className={`flex-1 rounded-xl px-4 py-3 text-sm font-semibold transition capitalize ${
                        formData.luggage === val
                          ? 'bg-black dark:bg-yellow-500 text-yellow-400 dark:text-black'
                          : 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700'
                      }`}
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-slate-400 mb-2">
                  {t('flexyRideSeat.comfort')}
                </label>
                <div className="flex gap-2">
                  {['quiet', 'chat', 'flexible'].map(val => (
                    <button
                      key={val}
                      type="button"
                      onClick={() =>
                        setFormData(prev => ({ ...prev, comfort: val }))
                      }
                      className={`flex-1 rounded-xl px-4 py-3 text-sm font-semibold transition capitalize ${
                        formData.comfort === val
                          ? 'bg-black dark:bg-yellow-500 text-yellow-400 dark:text-black'
                          : 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700'
                      }`}
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-slate-400 mb-2">
                  {t('flexyRideSeat.carBrand')}
                </label>
                <input
                  type="text"
                  value={formData.carBrand}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, carBrand: e.target.value }))
                  }
                  className="w-full rounded-2xl border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-800 px-4 py-3 text-lg text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none focus:border-black dark:focus:border-yellow-500"
                  placeholder={t('flexyRideSeat.carBrandPlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-slate-400 mb-2">
                  {t('flexyRideSeat.carModel')}
                </label>
                <input
                  type="text"
                  value={formData.carModel}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, carModel: e.target.value }))
                  }
                  className="w-full rounded-2xl border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-800 px-4 py-3 text-lg text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none focus:border-black dark:focus:border-yellow-500"
                  placeholder={t('flexyRideSeat.carModelPlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-slate-400 mb-2">
                  {t('flexyRideSeat.carYear')}
                </label>
                <input
                  type="number"
                  min="1900"
                  max={new Date().getFullYear() + 1}
                  value={formData.carYear}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, carYear: e.target.value }))
                  }
                  className="w-full rounded-2xl border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-800 px-4 py-3 text-lg text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none focus:border-black dark:focus:border-yellow-500"
                  placeholder={t('flexyRideSeat.carYearPlaceholder')}
                />
              </div>
            </div>
          </div>
        );

      case 'dateTime':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-black text-gray-900 dark:text-white">
              {t('flexyRideSeat.dateTimeTitle')}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-slate-400 mb-2">
                  {t('flexyRideSeat.date')}
                </label>
                <button
                  type="button"
                  onClick={() => setShowDatePicker(true)}
                  className="w-full flex items-center gap-3 rounded-2xl border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-800 px-4 py-3 text-lg text-gray-900 dark:text-white hover:border-black dark:hover:border-yellow-500 transition"
                >
                  <CalendarDays className="h-5 w-5 text-gray-500 dark:text-slate-400" />
                  <span className="flex-1 text-left">
                    {formData.date || t('flexyRideSeat.selectDate')}
                  </span>
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-slate-400 mb-2">
                  {t('flexyRideSeat.time')}
                </label>
                <button
                  type="button"
                  onClick={() => setShowTimePicker(true)}
                  className="w-full flex items-center gap-3 rounded-2xl border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-800 px-4 py-3 text-lg text-gray-900 dark:text-white hover:border-black dark:hover:border-yellow-500 transition"
                >
                  <Clock className="h-5 w-5 text-gray-500 dark:text-slate-400" />
                  <span className="flex-1 text-left">
                    {formData.time || t('flexyRideSeat.selectTime')}
                  </span>
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-slate-400 mb-2">
                  {t('flexyRideSeat.priceWithSeats', {
                    count: formData.seats || 1,
                  })}
                </label>
                <div className="flex items-center gap-2 rounded-2xl border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-800 px-4 py-3 focus-within:border-black dark:focus-within:border-yellow-500">
                  <DollarSign className="h-5 w-5 text-gray-500 dark:text-slate-400" />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, price: e.target.value }))
                    }
                    className="flex-1 bg-transparent text-lg text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none"
                    placeholder={t('flexyRideSeat.pricePlaceholder')}
                  />
                  <span className="text-sm text-gray-500 dark:text-slate-400">
                    CAD
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-slate-400 mb-2">
                  {t('flexyRideSeat.noteLabel')}
                </label>
                <textarea
                  value={formData.note}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, note: e.target.value }))
                  }
                  rows={4}
                  className="w-full rounded-2xl border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-800 px-4 py-3 text-lg text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none focus:border-black dark:focus:border-yellow-500"
                  placeholder={t('flexyRideSeat.notePlaceholder')}
                />
              </div>
            </div>
          </div>
        );

      case 'summary':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-black text-gray-900 dark:text-white">
              {t('flexyRideSeat.summaryTitle')}
            </h2>
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-xl">
                <p className="text-sm font-semibold text-gray-500 dark:text-slate-400 mb-2">
                  {t('flexyRideSeat.summaryRoute')}
                </p>
                <p className="text-base font-bold text-gray-900 dark:text-white">
                  {formData.from || t('home.from')} →{' '}
                  {formData.to || t('home.to')}
                </p>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-xl">
                <p className="text-sm font-semibold text-gray-500 dark:text-slate-400 mb-2">
                  {t('flexyRideSeat.summaryDeparture')}
                </p>
                <p className="text-base text-gray-900 dark:text-white">
                  {formData.date || t('flexyRideSeat.dateNotSet')} •{' '}
                  {formData.time || t('flexyRideSeat.timeNotSet')}
                </p>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-xl">
                <p className="text-sm font-semibold text-gray-500 dark:text-slate-400 mb-2">
                  {t('flexyRideSeat.summarySeatsPrice')}
                </p>
                <p className="text-base text-gray-900 dark:text-white">
                  {t('flexyRideSeat.summarySeatsPriceValue', {
                    seats: formData.seats || 1,
                    price: formData.price || '0',
                  })}
                </p>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-xl">
                <p className="text-sm font-semibold text-gray-500 dark:text-slate-400 mb-2">
                  {t('flexyRideSeat.summaryPreferences')}
                </p>
                <p className="text-sm text-gray-900 dark:text-white">
                  {t('flexyRideSeat.summaryPreferencesValue', {
                    smoking: formData.smoking,
                    pets: formData.pets,
                    luggage: formData.luggage,
                    comfort: formData.comfort,
                  })}
                </p>
              </div>
              {(formData.carBrand || formData.carModel || formData.carYear) && (
                <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-xl">
                  <p className="text-sm font-semibold text-gray-500 dark:text-slate-400 mb-2">
                    {t('flexyRideSeat.summaryVehicle')}
                  </p>
                  <p className="text-base text-gray-900 dark:text-white">
                    {formData.carBrand} {formData.carModel}{' '}
                    {formData.carYear ? `(${formData.carYear})` : ''}
                  </p>
                </div>
              )}
              {formData.note && (
                <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-xl">
                  <p className="text-sm font-semibold text-gray-500 dark:text-slate-400 mb-2">
                    {t('flexyRideSeat.summaryNote')}
                  </p>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {formData.note}
                  </p>
                </div>
              )}
            </div>
            <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-slate-700">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.agreeContact}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      agreeContact: e.target.checked,
                    }))
                  }
                  className="mt-1 h-5 w-5 rounded border-gray-300 text-black focus:ring-black dark:text-yellow-500"
                />
                <span className="text-sm text-gray-700 dark:text-slate-300">
                  {t('flexyRideSeat.agreeContact')}
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.agreePolicy}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      agreePolicy: e.target.checked,
                    }))
                  }
                  className="mt-1 h-5 w-5 rounded border-gray-300 text-black focus:ring-black dark:text-yellow-500"
                />
                <span className="text-sm text-gray-700 dark:text-slate-300">
                  {t('flexyRideSeat.agreePolicy')}
                </span>
              </label>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <MainNavbar />

      <main className="mx-auto max-w-3xl px-4 pt-28 pb-16 sm:px-6 lg:px-8">
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm font-semibold text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white transition"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('common.back')}
          </button>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-600 dark:text-slate-400">
              {t('requestDelivery.stepOf', {
                current: currentStep + 1,
                total: totalSteps,
              })}
            </span>
          </div>
          <div className="h-2 bg-gray-200 dark:bg-slate-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-black dark:bg-yellow-500 transition-all duration-300"
              style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Form Card */}
        <div className="rounded-3xl bg-white dark:bg-slate-900 p-8 shadow-lg border border-gray-200 dark:border-slate-800">
          {renderStep()}

          {/* Navigation Buttons */}
          <div className="mt-8 flex items-center justify-between gap-4">
            <button
              onClick={prevStep}
              disabled={currentStep === 0 || isSubmitting}
              className="flex items-center gap-2 px-6 py-3 rounded-xl border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white font-semibold hover:bg-gray-50 dark:hover:bg-slate-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('common.back')}
            </button>
            {currentStep === totalSteps - 1 ? (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-black dark:bg-yellow-500 text-yellow-400 dark:text-black font-semibold hover:bg-gray-900 dark:hover:bg-yellow-400 transition disabled:opacity-50"
              >
                {isSubmitting ? (
                  t('flexyRideSeat.creating')
                ) : editMode ? (
                  t('flexyRideSeat.updateListing')
                ) : (
                  <>
                    {t('flexyRideSeat.create')}{' '}
                    <CheckCircle className="h-4 w-4" />
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={nextStep}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-black dark:bg-yellow-500 text-yellow-400 dark:text-black font-semibold hover:bg-gray-900 dark:hover:bg-yellow-400 transition"
              >
                {t('common.next')}
                <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </main>

      {/* Date Picker Modal */}
      {showDatePicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              {t('flexyRideSeat.selectDateTitle')}
            </h3>
            <div className="mb-4">
              <input
                type="date"
                value={selectedDate.toISOString().split('T')[0]}
                onChange={e => setSelectedDate(new Date(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDatePicker(false)}
                className="flex-1 px-4 py-2 bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white rounded-lg font-semibold hover:bg-gray-200 dark:hover:bg-slate-700"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => {
                  const formatted = formatDate(selectedDate);
                  setFormData(prev => ({ ...prev, date: formatted }));
                  setShowDatePicker(false);
                }}
                className="flex-1 px-4 py-2 bg-black dark:bg-yellow-500 text-yellow-400 dark:text-black rounded-lg font-semibold hover:bg-gray-900 dark:hover:bg-yellow-400"
              >
                {t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Time Picker Modal */}
      {showTimePicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              {t('flexyRideSeat.selectTimeTitle')}
            </h3>
            <div className="mb-4">
              <input
                type="time"
                value={formatTime(selectedTime)}
                onChange={e => {
                  const [hours, minutes] = e.target.value.split(':');
                  const newTime = new Date(selectedTime);
                  newTime.setHours(parseInt(hours), parseInt(minutes));
                  setSelectedTime(newTime);
                }}
                className="w-full px-4 py-2 border border-gray-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowTimePicker(false)}
                className="flex-1 px-4 py-2 bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white rounded-lg font-semibold hover:bg-gray-200 dark:hover:bg-slate-700"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => {
                  const formatted = formatTime(selectedTime);
                  setFormData(prev => ({ ...prev, time: formatted }));
                  setShowTimePicker(false);
                }}
                className="flex-1 px-4 py-2 bg-black dark:bg-yellow-500 text-yellow-400 dark:text-black rounded-lg font-semibold hover:bg-gray-900 dark:hover:bg-yellow-400"
              >
                {t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

